import { useId } from 'react'

import { cn } from '@/lib/cn'

/**
 * The `d/` mark: a lowercase `d` stem+bowl crossed by a diagonal slash, read
 * as `d` + `%` — dizcant. Gradient-filled (not `currentColor`), so `text-*`
 * utilities at the call site are inert; size with `className` (`size-*`).
 *
 * `viewBox="-27.5 -41.5 300 300"` is the mark's tight bbox — including stroke
 * extents (bowl stroke-width 26, slash stroke-width 28 with round caps) —
 * centered with even padding, computed once so the geometry below needs no
 * wrapping transform.
 *
 * Two deliberate corrections from the source design file:
 *  - `gradientUnits="userSpaceOnUse"`: the default (`objectBoundingBox`) gives
 *    every element its own independent 0→1 sweep, so the stem ramps near
 *    vertically while each dot holds a full indigo→cyan ramp in 32 units —
 *    the gradient repeats instead of flowing once across the mark. Pinning
 *    both gradients to the mark's own coordinate space fixes that. The slash
 *    needs its own axis (not the `d`'s) — projected onto the `d`'s diagonal
 *    its endpoints span t=0.595→0.607, i.e. flat colour.
 *  - The two orbit dots (the `%`'s corners) are dropped below 96px. Their gap
 *    to the slash is 3.92 user units — 0.31px at this mark's only shipped
 *    size, 24px — and antialiases shut, reading as one lumpy diagonal rather
 *    than two shapes. Bowl + stem + slash alone still reads as `d/`.
 */
export function DizcantMark({ className }: { className?: string }) {
  // React 19 emits ids like `_R_1_`; strip non-word chars so the id is a
  // valid `url(#…)` fragment regardless of React's id format, and unique per
  // mount — the header and footer both render <Logo /> on the same page.
  const uid = useId().replace(/[^a-zA-Z0-9]/g, '')
  const dGradientId = `dizcant-d-${uid}`
  const slashGradientId = `dizcant-slash-${uid}`

  return (
    <svg
      viewBox="-27.5 -41.5 300 300"
      fill="none"
      aria-hidden="true"
      focusable="false"
      className={cn('dizcant-mark size-6', className)}
    >
      <defs>
        <linearGradient
          id={dGradientId}
          gradientUnits="userSpaceOnUse"
          x1="-13"
          y1="5"
          x2="204"
          y2="200"
        >
          <stop offset="0" stopColor="var(--brand-gradient-from)" />
          <stop offset="1" stopColor="var(--brand-gradient-to)" />
        </linearGradient>
        <linearGradient
          id={slashGradientId}
          gradientUnits="userSpaceOnUse"
          x1="-13"
          y1="219"
          x2="258"
          y2="-2"
        >
          <stop offset="0" stopColor="var(--brand-gradient-from)" />
          <stop offset="1" stopColor="var(--brand-gradient-to)" />
        </linearGradient>
      </defs>

      <rect
        x="100"
        y="5"
        width="28"
        height="195"
        rx="14"
        fill={`url(#${dGradientId})`}
      />
      <path
        d="M 100,105 C 100,68 68,48 38,48 C 14,48 0,68 0,105 C 0,145 14,165 38,165 C 68,165 100,145 100,105 Z"
        stroke={`url(#${dGradientId})`}
        strokeWidth={26}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M 92,205 L 244,12"
        stroke={`url(#${slashGradientId})`}
        strokeWidth={28}
        strokeLinecap="round"
      />
    </svg>
  )
}

interface LogoProps {
  /** Hide the wordmark, showing the mark alone (e.g. compact contexts). */
  markOnly?: boolean
  className?: string
}

/** Mark + live-text wordmark lockup. */
export function Logo({ markOnly = false, className }: LogoProps) {
  return (
    <span className={cn('inline-flex items-center gap-2', className)}>
      <DizcantMark />
      {!markOnly && (
        <span className="font-display text-headline-md text-fg font-bold tracking-tight lowercase">
          dizcant
        </span>
      )}
    </span>
  )
}
